import type { ReactNode } from "react";
import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import type { FindingType, Severity } from "@/components/finding-badges";
import type { FindingDesignCauseItem } from "@/components/experience/finding-observation";

export interface ZenFindingItem {
  id: string;
  title: string | null;
  description: string;
  type: FindingType;
  severity: Severity | null;
  uiFact?: string | null;
  consequence?: string | null;
  what?: string | null;
  why?: string | null;
  basis?: string | null;
  screen?: string | null;
  breaksRule?: string | null;
  breaksFile?: string | null;
  recommendation?: string | null;
  designCauses?: FindingDesignCauseItem[] | null;
  personaQuote?: string | null;
  personaName?: string | null;
  walkthroughId?: string | null;
  stepNumber?: number | null;
  stepTitle?: string | null;
  targetBox?: TargetBox | null;
  pageUrl?: string | null;
  canonicalFindingId?: string | null;
  triageStatus?: string | null;
  fixStatus?: string | null;
  lens?: string | null;
  trackerLinks?: Array<{
    id: string;
    provider: string;
    kind: string;
    displayKey: string | null;
    url: string | null;
    category: string;
    creationState: string;
  }>;
}

export interface ZenCarouselCanvasProps {
  finding: ZenFindingItem;
  showHighlights: boolean;
  onToggleHighlights: () => void;
  onSelectHighlight?: (id: string) => void;
  focusMode?: "page" | "target";
  onToggleFocusMode?: () => void;
  className?: string;
}

export interface ZenCarouselSidebarProps {
  finding: ZenFindingItem;
  projectId: string;
  canEdit: boolean;
  hasLinkedRepo?: boolean;
  isTrackerConfigured: boolean;
  activeProvider: string | null;
  exportConfig?: unknown;
  createIssuePending?: boolean;
  onCreateIssue?: (finding: ZenFindingItem) => void;
  onLinkExisting?: (canonicalId: string) => void;
  onViewInTrace?: () => void;
  onClose?: () => void;
}

export function enrichFindingsWithStepData(
  findings: Array<{
    id: string;
    title?: string | null;
    description: string;
    type: FindingType | string;
    severity?: Severity | string | null;
    uiFact?: string | null;
    consequence?: string | null;
    what?: string | null;
    why?: string | null;
    basis?: string | null;
    screen?: string | null;
    steps?: number[] | null;
    breaksRule?: string | null;
    breaksFile?: string | null;
    recommendation?: string | null;
    designCauses?: FindingDesignCauseItem[] | null;
    walkthroughStepId?: string | null;
    stepNumber?: number | null;
    walkthroughId?: string | null;
    targetBox?: TargetBox | null;
    canonicalFindingId?: string | null;
    triageStatus?: string | null;
    fixStatus?: string | null;
    lens?: any;
    trackerLinks?: Array<{
      id: string;
      provider: string;
      kind: string;
      displayKey: string | null;
      url: string | null;
      category: string;
      creationState: string;
    }>;
  }>,
  walkthroughSteps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    pageUrl?: string | null;
  }>,
  personaName?: string | null,
  walkthroughId?: string,
): ZenFindingItem[] {
  return findings.map((f) => {
    const fallbackStepNum = f.stepNumber ?? f.steps?.[0] ?? null;
    const step = walkthroughSteps.find(
      (s) =>
        (f.walkthroughStepId && s.id === f.walkthroughStepId) ||
        (typeof fallbackStepNum === "number" &&
          s.stepNumber === fallbackStepNum),
    );

    return {
      id: f.id,
      title: f.title ?? null,
      description: f.description,
      type: f.type as FindingType,
      severity: (f.severity as Severity) ?? null,
      uiFact: f.uiFact,
      consequence: f.consequence,
      what: f.what,
      why: f.why,
      basis: f.basis,
      screen: f.screen,
      breaksRule: f.breaksRule,
      breaksFile: f.breaksFile,
      recommendation: f.recommendation,
      designCauses: f.designCauses ?? null,
      personaQuote: step?.personaReasoning ?? null,
      personaName: personaName ?? null,
      walkthroughId: f.walkthroughId ?? walkthroughId ?? null,
      stepNumber: fallbackStepNum ?? step?.stepNumber ?? null,
      stepTitle: step?.title ?? null,
      targetBox: f.targetBox ?? null,
      pageUrl: step?.pageUrl ?? null,
      canonicalFindingId: f.canonicalFindingId ?? null,
      triageStatus: f.triageStatus ?? null,
      fixStatus: f.fixStatus ?? null,
      lens: f.lens ?? null,
      trackerLinks: f.trackerLinks,
    };
  });
}
